import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, Download, Copy, CheckCircle, Loader2, Instagram, Youtube, Linkedin, Clock, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExportPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const script = location.state?.script;
  const [doctor, setDoctor] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!script) {
      toast.error('No script selected');
      navigate(`/library/${doctorId}`);
      return;
    }
    fetchDoctorAndGenerate();
  }, []);

  const fetchDoctorAndGenerate = async () => {
    setLoading(true);
    try {
      const doctorRes = await axios.get(`${API}/doctors/${doctorId}`);
      setDoctor(doctorRes.data);
      
      // Generate export content
      const exportRes = await axios.post(`${API}/export/generate`, {
        script_id: script.id,
        topic: script.topic,
        specialty: doctorRes.data.specialty,
        country: doctorRes.data.country
      });
      
      setExportData(exportRes.data);
      toast.success('Distribution content generated!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate distribution content');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleDownloadScript = () => {
    const scriptText = `
=== ${script.topic} ===

HOOK:
${script.hook}

EXPLANATION:
${script.explanation}

CALL TO ACTION:
${script.cta}

DISCLAIMER:
${script.disclaimer}

=== DISTRIBUTION ===

CAPTION:
${exportData?.caption || ''}

HASHTAGS:
${exportData?.hashtags?.join(' ') || ''}

BEST POSTING TIME:
${exportData?.posting_time || ''}
    `.trim();

    const blob = new Blob([scriptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${script.topic.replace(/\s+/g, '_')}_script.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Script downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#2C5F44] animate-spin mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-[#5C7063]">Generating distribution content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/library/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="export-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Export & Distribution
            </span>
          </div>
          <Button
            onClick={handleDownloadScript}
            className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
            data-testid="export-download-btn"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Download Full Script
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {script?.topic}
          </h1>
          <p className="text-lg text-[#5C7063]">
            Ready to distribute on social media
          </p>
        </motion.div>

        <div className="space-y-6">
          {/* Caption */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#1C2A21]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Caption
              </h2>
              <Button
                onClick={() => copyToClipboard(exportData?.caption || '', 'Caption')}
                variant="outline"
                size="sm"
                className="rounded-full border-[#2C5F44]/20"
                data-testid="export-copy-caption-btn"
              >
                <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Copy
              </Button>
            </div>
            <p className="text-[#1C2A21] bg-[#F4F1EA] p-4 rounded-xl">
              {exportData?.caption || 'Generating...'}
            </p>
          </motion.div>

          {/* Hashtags */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Hash className="w-6 h-6 text-[#2C5F44]" strokeWidth={1.5} />
                <h2 className="text-2xl font-bold text-[#1C2A21]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Hashtags
                </h2>
              </div>
              <Button
                onClick={() => copyToClipboard(exportData?.hashtags?.join(' ') || '', 'Hashtags')}
                variant="outline"
                size="sm"
                className="rounded-full border-[#2C5F44]/20"
                data-testid="export-copy-hashtags-btn"
              >
                <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Copy
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {exportData?.hashtags?.map((tag, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-[#82A08B]/20 text-[#2C5F44] rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              )) || <p className="text-[#5C7063]">Generating...</p>}
            </div>
          </motion.div>

          {/* Posting Time & Platform Tips */}
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
            >
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-6 h-6 text-[#2C5F44]" strokeWidth={1.5} />
                <h2 className="text-xl font-bold text-[#1C2A21]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Best Posting Time
                </h2>
              </div>
              <p className="text-[#1C2A21]">
                {exportData?.posting_time || 'Generating...'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
            >
              <h2 className="text-xl font-bold text-[#1C2A21] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Recommended Platforms
              </h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-[#1C2A21]">
                  <Instagram className="w-5 h-5 text-pink-500" strokeWidth={1.5} />
                  <span>Instagram Reels</span>
                </div>
                <div className="flex items-center gap-3 text-[#1C2A21]">
                  <Youtube className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                  <span>YouTube Shorts</span>
                </div>
                <div className="flex items-center gap-3 text-[#1C2A21]">
                  <Linkedin className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  <span>LinkedIn</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Platform Tips */}
          {exportData?.platform_tips && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-gradient-to-br from-[#2C5F44] to-[#244b36] rounded-3xl p-8 text-white"
            >
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Platform Tips
              </h2>
              <p className="text-white/90">{exportData.platform_tips}</p>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex gap-4"
          >
            <Button
              onClick={() => navigate(`/video/${doctorId}`, { state: { script } })}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full flex-1"
              data-testid="export-create-video-btn"
            >
              Create Video
            </Button>
            <Button
              onClick={() => navigate(`/library/${doctorId}`)}
              variant="outline"
              className="rounded-full border-[#2C5F44]/20 flex-1"
              data-testid="export-back-library-btn"
            >
              Back to Library
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}